<template>
  <div class="wrap">
    <div class="preview" @click="openModal" title="Click to change icon">
      <HeroIcon :name="iconName || 'CubeTransparentIcon'" :iconStyle="iconName ? iconStyle : 'outline'" :color="iconName ? iconColor : '#9ca3af'" :size="22" />
    </div>

    <Modal v-if="showModal" title="Choose icon" @close="cancelModal">
      <div class="pickerBody">
        <div class="pickerControls">
          <div class="finalPreview" v-if="draftName">
            <HeroIcon :name="draftName" :iconStyle="draftStyleResolved" :color="draftColor || '#111827'" :size="32" />
          </div>

          <div class="group">
            <div class="label">Style</div>
            <div class="seg">
              <button class="seg-btn" type="button" :class="{ active: draftStyleResolved === 'outline' }" @click="draftStyle = 'outline'">
                Outline
              </button>
              <button class="seg-btn" type="button" :class="{ active: draftStyleResolved === 'solid' }" @click="draftStyle = 'solid'">
                Solid
              </button>
            </div>
          </div>

          <div class="group" style="flex:1;">
            <div class="label">Color</div>
            <div class="palette">
              <button
                v-for="c in COLORS"
                :key="c"
                class="swatch"
                type="button"
                :class="{ selected: draftColor === c }"
                @click="draftColor = c"
                :title="c"
              >
                <span class="swatch-dot" :style="{ background: c }" />
              </button>
            </div>
          </div>

          <button v-if="draftName" class="btn" type="button" @click="resetDraft" style="align-self:flex-end;">Reset</button>
        </div>

        <div class="search">
          <input class="input" v-model="query" placeholder="Search icons (e.g. user, bolt, building)" />
          <div class="muted" style="font-size:12px;">{{ filteredNames.length }} icons</div>
        </div>

        <div class="grid">
          <button
            v-for="n in filteredNames"
            :key="n"
            class="icon-tile"
            type="button"
            :class="{ chosen: draftName === n }"
            @click="chooseDraft(n)"
          >
            <HeroIcon :name="n" iconStyle="outline" color="#6b7280" :size="22" />
            <div class="icon-name">{{ displayName(n) }}</div>
          </button>
        </div>
      </div>

      <template #footer>
        <button class="btn" type="button" @click="cancelModal">Cancel</button>
        <button class="btn primary" type="button" @click="confirmModal">Select</button>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import * as SolidIcons from '@heroicons/vue/24/solid'
import * as OutlineIcons from '@heroicons/vue/24/outline'
import Modal from './Modal.vue'
import HeroIcon from './HeroIcon.vue'

const props = defineProps({
  iconName: { type: String, default: null },
  iconStyle: { type: String, default: null },
  iconColor: { type: String, default: null },
})

const emit = defineEmits(['update:iconName', 'update:iconStyle', 'update:iconColor'])

const showModal = ref(false)
const query = ref('')

const draftName = ref(null)
const draftStyle = ref(null)
const draftColor = ref(null)

const COLORS = [
  '#111827',
  '#6b7280',
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#84cc16',
  '#22c55e',
  '#14b8a6',
  '#06b6d4',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#d946ef',
  '#ec4899',
]

const draftStyleResolved = computed(() => (draftStyle.value === 'solid' ? 'solid' : 'outline'))

const iconNames = computed(() => {
  const solid = Object.keys(SolidIcons)
  const out = Object.keys(OutlineIcons)
  const outSet = new Set(out)
  return solid
    .filter((k) => k.endsWith('Icon') && outSet.has(k))
    .sort((a, b) => a.localeCompare(b))
})

const filteredNames = computed(() => {
  const q = (query.value || '').trim().toLowerCase()
  if (!q) return iconNames.value
  return iconNames.value.filter((n) => n.toLowerCase().includes(q))
})

function displayName(n) {
  return n.replace(/Icon$/, '')
}

function openModal() {
  draftName.value = props.iconName || null
  draftStyle.value = props.iconStyle || 'outline'
  draftColor.value = props.iconColor || null
  query.value = ''
  showModal.value = true
}

function cancelModal() {
  showModal.value = false
}

function confirmModal() {
  emit('update:iconName', draftName.value)
  emit('update:iconStyle', draftName.value ? draftStyle.value : null)
  emit('update:iconColor', draftName.value ? draftColor.value : null)
  showModal.value = false
}

function chooseDraft(name) {
  draftName.value = name
  if (!draftStyle.value) draftStyle.value = 'outline'
}

function resetDraft() {
  draftName.value = null
  draftStyle.value = 'outline'
  draftColor.value = '#6b7280'
}
</script>

<style scoped>
.wrap {
  display: inline-flex;
}

.preview {
  width: 36px;
  height: 36px;
  border: 1px solid var(--border);
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: var(--badge-bg);
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.preview:hover {
  border-color: var(--focus-border);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.pickerBody {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.finalPreview {
  width: 48px;
  height: 48px;
  border: 1px solid var(--border);
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: var(--badge-bg);
  flex-shrink: 0;
}

.pickerControls {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex-wrap: wrap;
}

.group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.label {
  font-size: 12px;
  color: var(--muted);
}

.seg {
  display: inline-flex;
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
}

.seg-btn {
  height: 32px;
  padding: 0 10px;
  border: 0;
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
}

.seg-btn + .seg-btn {
  border-left: 1px solid var(--border);
}

.seg-btn.active {
  background: var(--row-hover-bg);
}

.palette {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.swatch {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
  display: grid;
  place-items: center;
}

.swatch.selected {
  border-color: var(--text);
}

.swatch-dot {
  width: 16px;
  height: 16px;
  border-radius: 999px;
}

.search {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
  max-height: 46vh;
  overflow: auto;
  padding: 2px;
}

.icon-tile {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 10px;
  padding: 10px 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.icon-tile:hover {
  border-color: var(--focus-border);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.icon-tile.chosen {
  border-color: var(--selection-border);
  background: var(--selection-bg);
}

.icon-name {
  font-size: 11px;
  color: var(--muted);
  text-align: center;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
